import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, BadgeCheck, TrendingUp, Users } from "lucide-react";

const LINES = [
  { text: "Domina la", accent: false },
  { text: "contabilidad.", accent: true },
  { text: "Lidera las finanzas.", accent: false },
];

const STATS = [
  { icon: Users, value: "+4.500", label: "egresados" },
  { icon: TrendingUp, value: "98%", label: "inserción laboral" },
  { icon: BadgeCheck, value: "4.9/5", label: "valoración" },
];

export const Hero = ({ onEnroll }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  return (
    <section id="hero" data-testid="hero-section" ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16">
      <div className="absolute inset-0 hero-glow" />
      <div className="absolute inset-0 opacity-[0.04] dark:opacity-[0.06]" style={{ backgroundImage: "linear-gradient(currentColor 1px, transparent 1px), linear-gradient(90deg, currentColor 1px, transparent 1px)", backgroundSize: "72px 72px" }} />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center w-full">
        <motion.div className="lg:col-span-7" style={{ y: textY }}>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="text-xs uppercase font-mono tracking-[0.28em] text-emerald-600 dark:text-emerald-400 font-semibold mb-6"
          >
            Capacitación contable · tributaria · financiera
          </motion.p>

          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05]">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.accent ? "font-serif italic font-semibold text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-amber-500" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.25 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.7 }}
            className="mt-7 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl"
          >
            Formación en vivo para contadores y equipos administrativos: NIIF, tributación,
            auditoría y herramientas digitales, con casos reales y mentoría directa de expertos Big Four.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-btn"
              onClick={() => onEnroll()}
              className="rounded-full bg-primary text-primary-foreground font-semibold px-8 py-4 text-sm sm:text-base hover:brightness-110 hover:shadow-xl hover:shadow-emerald-500/25 active:scale-95 transition-all"
            >
              Inscribirme ahora
            </button>
            <a
              href="#cursos"
              data-testid="hero-catalog-link"
              onClick={(e) => { e.preventDefault(); document.querySelector("#cursos")?.scrollIntoView({ behavior: "smooth" }); }}
              className="group inline-flex items-center gap-2 rounded-full border border-border px-8 py-4 text-sm sm:text-base font-medium hover:border-primary/60 transition-colors"
            >
              Ver catálogo
              <ArrowDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-12 flex flex-wrap gap-8"
          >
            {STATS.map((s) => (
              <div key={s.label} data-testid={`hero-stat-${s.label.replace(/\s/g, "-")}`} className="flex items-center gap-3">
                <s.icon className="w-5 h-5 text-emerald-500" />
                <div>
                  <div className="font-display font-bold text-xl leading-none">{s.value}</div>
                  <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          className="lg:col-span-5 relative hidden lg:block"
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, duration: 1, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="relative rounded-3xl overflow-hidden border border-border shadow-2xl aspect-[4/5]">
            <motion.img
              src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&q=80&w=1200"
              alt="Escritorio de trabajo contable"
              className="w-full h-full object-cover scale-110"
              style={{ y: imgY }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          </div>
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="absolute -left-10 bottom-10 backdrop-blur-xl bg-background/85 border border-white/10 rounded-2xl p-5 shadow-2xl w-56"
          >
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-500 mb-1">Próxima cohorte</p>
            <p className="font-display font-bold text-lg leading-tight">Agosto 2026</p>
            <p className="text-xs text-muted-foreground mt-1.5">Cupos limitados · 20 estudiantes por grupo</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
